import {getConfig} from '../support/Config';
import logger from '../utils/logger';
import notificationStore from '../store/NotificationStore';
import legalConsentStore from '../store/LegalConsentStore';

interface UserInfo {
    preferred_username?: string;
    name?: string;
    email?: string;
    given_name?: string;
    family_name?: string;

    [key: string]: any;
}

interface AuthStatus {
    authenticated: boolean;
    expiresIn?: number;
    roles?: string[];
    /** Пароль принят, но у аккаунта включена 2FA — нужен код из аутентификатора. */
    mfaRequired?: boolean;
    /** Одноразовый токен незавершённого входа; отдаётся вместе с mfaRequired. */
    mfaToken?: string;
    /**
     * Есть непринятые правовые документы (2026-08-26_legal-documents, §9).
     * Поле отсутствует, если бэкенд не смог проверить журнал — это не «нет».
     */
    legalPending?: boolean;
}

// Periodically rotate the (httpOnly) access cookie while the tab is open.
const REFRESH_INTERVAL_MS = 4 * 60 * 1000;
// A single transient refresh failure (flaky network, brief 5xx) must NOT log the user out.
// Retry with a short grace delay before giving up; only a definitive auth failure resets.
const REFRESH_MAX_ATTEMPTS = 2;
const REFRESH_RETRY_DELAY_MS = 1000;

/**
 * Cookie-based auth. Access/refresh tokens live in httpOnly cookies set by the backend and are
 * never readable by JavaScript (no localStorage). The browser sends them automatically because
 * every request uses credentials. Roles come from the backend (the SPA can't decode the token).
 */
class AuthService {
    private authenticatedFlag: boolean = false;
    private roles: string[] = [];
    private userinfo: UserInfo = {};
    private initialized: boolean = false;
    private _refreshPromise: Promise<void> | null = null;
    private _refreshIntervalId: ReturnType<typeof setInterval> | null = null;
    private _lastRefreshAt: number = 0;
    /** Одноразовый токен незавершённого входа: живёт только между шагом 1 и шагом 2. */
    private _mfaToken: string = '';

    get authenticated(): boolean {
        return this.authenticatedFlag;
    }

    get isInitialized(): boolean {
        return this.initialized;
    }

    init = async (): Promise<void> => {
        if (this.initialized) {
            return;
        }
        // On page load there is no in-memory state — ask the backend to refresh using the
        // refresh cookie. Success means we are logged in; failure means we are not.
        try {
            await this._refresh();
            await this._loadUserInfo();
            this._startRefreshInterval();
        } catch {
            this._reset();
        }
        this.initialized = true;
        logger.debug('AuthService: init complete', {authenticated: this.authenticatedFlag});
    };

    isAuthenticated = (): boolean => this.authenticatedFlag;

    /** Tokens are httpOnly — not available to JavaScript. */
    getToken = (): string => '';

    hasRole = (role: string): boolean => this.authenticatedFlag && this.roles.includes(role);

    getUserInfo = (): UserInfo => this.userinfo || {};

    /**
     * Первый шаг входа. Если у аккаунта включена 2FA, сессия ещё не создана:
     * возвращается {mfaRequired: true}, а вход завершает {@link verifyOtp}.
     */
    async passwordLogin(username: string, password: string, rememberMe?: boolean,
                        captchaToken?: string): Promise<{ mfaRequired: boolean }> {
        const resp = await fetch(getConfig('apiBaseUrl') + '/auth/login', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            // captchaToken — токен SmartCaptcha с формы входа; при выключенной капче
            // сервер поле игнорирует (auth-registration-recovery, FR-19).
            body: JSON.stringify({username, password, rememberMe: rememberMe || false, captchaToken: captchaToken || ''})
        });

        if (!resp.ok) {
            throw new Error('INVALID_CREDENTIALS');
        }

        const data: AuthStatus = await resp.json();
        if (data.mfaRequired) {
            this._mfaToken = data.mfaToken || '';
            logger.debug('AuthService: MFA required, awaiting OTP');
            return {mfaRequired: true};
        }

        await this._completeLogin(data);
        return {mfaRequired: false};
    }

    /**
     * Второй шаг входа: код из аутентификатора либо recovery-код.
     * Бросает Error('INVALID_OTP') при неверном коде и Error('MFA_EXPIRED'),
     * если подтверждать уже нечего — нужен повторный ввод пароля.
     */
    async verifyOtp(code: string): Promise<void> {
        if (!this._mfaToken) {
            throw new Error('MFA_EXPIRED');
        }
        const resp = await fetch(getConfig('apiBaseUrl') + '/auth/login/otp', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({mfaToken: this._mfaToken, code})
        });

        if (!resp.ok) {
            const reason = await this._mapOtpError(resp);
            if (reason === 'MFA_EXPIRED') {
                this._mfaToken = '';
            }
            throw new Error(reason);
        }

        this._mfaToken = '';
        await this._completeLogin(await resp.json());
    }

    /** Сброс незавершённого входа (пользователь закрыл окно ввода кода). */
    cancelMfa(): void {
        this._mfaToken = '';
    }

    /** Общий хвост успешного входа для обоих шагов. */
    private async _completeLogin(data: AuthStatus): Promise<void> {
        this.authenticatedFlag = !!data.authenticated;
        this.roles = data.roles || [];
        this._applyLegalPending(data);
        await this._loadUserInfo();
        this._startRefreshInterval();
    }

    /** Refresh the auth cookies via the backend. Throws if not authenticated. */
    async updateToken(): Promise<void> {
        if (this._refreshPromise) {
            return this._refreshPromise;
        }
        this._refreshPromise = this._refresh().finally(() => {
            this._refreshPromise = null;
        });
        return this._refreshPromise;
    }

    async loadUserInfo(): Promise<void> {
        await this._loadUserInfo();
    }

    async logout(): Promise<void> {
        this._stopRefreshInterval();
        try {
            await fetch(getConfig('apiBaseUrl') + '/auth/logout', {
                method: 'POST',
                credentials: 'include'
            });
        } catch {
            // ignore network errors on logout
        }
        this._reset();
    }

    /**
     * Refresh the auth cookies, tolerating a single transient failure. We only call
     * {@link _reset} (which logs the user out) when the failure is definitive — a rejected
     * refresh token (HTTP 4xx) — or when all retry attempts are exhausted. Network blips and
     * brief 5xx are retried after a short grace delay, so a flaky moment around the 5-minute
     * access-token boundary no longer drops the session.
     */
    private async _refresh(): Promise<void> {
        let lastError: unknown;
        for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt++) {
            try {
                await this._refreshOnce();
                return;
            } catch (error) {
                lastError = error;
                if (this._isDefinitiveAuthFailure(error) || attempt >= REFRESH_MAX_ATTEMPTS) {
                    break;
                }
                await this._delay(REFRESH_RETRY_DELAY_MS);
            }
        }
        this._reset();
        throw lastError instanceof Error ? lastError : new Error('Refresh failed');
    }

    /** Single refresh HTTP call. Throws on failure WITHOUT resetting (the caller decides). */
    private async _refreshOnce(): Promise<void> {
        const resp = await fetch(getConfig('apiBaseUrl') + '/auth/refresh', {
            method: 'POST',
            credentials: 'include'
        });
        if (!resp.ok) {
            const error = new Error('Refresh failed') as Error & { status?: number };
            error.status = resp.status;
            throw error;
        }
        const data: AuthStatus = await resp.json();
        this.authenticatedFlag = !!data.authenticated;
        this.roles = data.roles || this.roles;
        this._applyLegalPending(data);
        this._lastRefreshAt = Date.now();
    }

    /**
     * Сигнал о непринятых документах приходит с каждым входом/refresh — отдельный
     * запрос не нужен. Отсутствующее поле состояние не меняет: бэкенд не смог
     * проверить журнал, и «нет» это не означает.
     */
    private _applyLegalPending(data: AuthStatus): void {
        if (typeof data.legalPending === 'boolean') {
            legalConsentStore.setPending(data.legalPending);
        }
    }

    /** A 4xx means the refresh token is rejected — retrying cannot help, so log out now. */
    private _isDefinitiveAuthFailure(error: unknown): boolean {
        const status = (error as { status?: number })?.status;
        return typeof status === 'number' && status >= 400 && status < 500;
    }

    private _delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private async _loadUserInfo(): Promise<void> {
        try {
            const resp = await fetch(getConfig('apiBaseUrl') + '/auth/userinfo', {
                method: 'GET',
                credentials: 'include'
            });
            if (resp.ok) {
                this.userinfo = await resp.json();
            }
        } catch (error) {
            logger.debug('AuthService: failed to load userinfo', {error: (error as Error)?.message});
        }
    }

    private _reset(): void {
        this._stopRefreshInterval();
        this._mfaToken = '';
        this.authenticatedFlag = false;
        this.roles = [];
        this.userinfo = {};
        notificationStore.reset();
        legalConsentStore.reset();
    }

    private _startRefreshInterval(): void {
        this._stopRefreshInterval();
        this._lastRefreshAt = Date.now();
        this._refreshIntervalId = setInterval(() => this._proactiveRefresh(), REFRESH_INTERVAL_MS);
        // Background tabs throttle setInterval (sometimes to far less than once per interval),
        // so a timer alone can let the access cookie expire silently. Also refresh when the
        // tab regains visibility, covering the case where the timer was starved.
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._onVisibilityChange);
        }
    }

    private _stopRefreshInterval(): void {
        if (this._refreshIntervalId) {
            clearInterval(this._refreshIntervalId);
            this._refreshIntervalId = null;
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
        }
    }

    // Stable reference so removeEventListener works.
    private _onVisibilityChange = (): void => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            return;
        }
        // Only refresh if the timer may have been starved while backgrounded.
        if (Date.now() - this._lastRefreshAt >= REFRESH_INTERVAL_MS) {
            this._proactiveRefresh();
        }
    };

    private _proactiveRefresh(): void {
        if (!this.authenticatedFlag) {
            return;
        }
        // _refresh already resets on definitive/exhausted failure; just swallow the rejection.
        this.updateToken().catch((err) => {
            logger.debug('AuthService: proactive refresh failed', {error: (err as Error)?.message});
        });
    }

    /**
     * Второй фактор теперь наш, поэтому угадывать причину по тексту Keycloak не нужно:
     * backend различает «неверный код» и «челлендж истёк» явными кодами ошибок.
     */
    private async _mapOtpError(resp: Response): Promise<string> {
        try {
            const err = await resp.json();
            if (err?.error === 'MFA_EXPIRED') {
                return 'MFA_EXPIRED';
            }
        } catch {
            // тело может быть пустым — тогда считаем код неверным
        }
        return 'INVALID_OTP';
    }
}

const authService = new AuthService();
export default authService;
